import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';

const MODEL_PATH = '/models/mortar.glb';

function MortarModel({ rotationDelta }) {
  const { scene } = useGLTF(MODEL_PATH);

  useFrame((state, delta) => {
    // Apply the delta rotation smoothly
    scene.rotation.y += rotationDelta * delta * 60; // adjust multiplier for feel (higher = faster)
  });

  return (
    <primitive
      object={scene}
      scale={6.8}
      position={[0, 0, 0]}
      rotation={[0.2, Math.PI / 4, 0]}
    />
  );
}

export default function FloatingMortar() {
  const [rotationDelta, setRotationDelta] = useState(0);
  const lastDelta = useRef(0);
  const rafRef = useRef(null);
  const isTouchDevice = useRef('ontouchstart' in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    let prevY = window.scrollY;
    let touchStartY = 0;

    const handleWheel = (e) => {
      // Desktop scroll
      const delta = e.deltaY > 0 ? 0.015 : -0.015; // tune sensitivity
      setRotationDelta(delta);

      // Decay back to 0
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setRotationDelta((prev) => prev * 0.92); // smooth decay
      });
    };

    const handleTouchStart = (e) => {
      if (e.touches.length > 0) {
        touchStartY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        const currentY = e.touches[0].clientY;
        const deltaY = touchStartY - currentY; // positive = finger moving up (usually scroll down)

        // Convert touch delta to rotation delta (tune multiplier)
        const touchDelta = deltaY * 0.0008; // smaller value = less sensitive

        setRotationDelta(touchDelta);

        // Update start for continuous drag feel
        touchStartY = currentY;
      }
    };

    const handleTouchEnd = () => {
      // Smooth decay after touch ends
      const decay = () => {
        setRotationDelta((prev) => {
          const next = prev * 0.94;
          if (Math.abs(next) > 0.0001) {
            rafRef.current = requestAnimationFrame(decay);
            return next;
          }
          return 0;
        });
      };
      decay();
    };

    // Desktop
    window.addEventListener('wheel', handleWheel, { passive: true });

    // Mobile / Tablet
    if (isTouchDevice.current) {
      window.addEventListener('touchstart', handleTouchStart, { passive: true });
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (isTouchDevice.current) {
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      className="w-80 h-80 sm:w-96 sm:h-96 md:w-[500px] md:h-[500px] lg:w-[580px] lg:h-[580px]
                 rounded-3xl overflow-hidden pointer-events-none"
    >
      <Canvas
        camera={{ position: [3, 2, 10], fov: 36 }}
        gl={{ antialias: true, alpha: true }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={1.2} />
        <directionalLight position={[4, 10, 6]} intensity={1.5} />

        <Suspense fallback={null}>
          <MortarModel rotationDelta={rotationDelta} />
        </Suspense>
      </Canvas>
    </div>
  );
}